import React, { Suspense, createContext, useContext, useMemo } from 'react';
import { matchPath } from 'react-router-dom';
import { LoadingSpinner } from '@/components/loaders/loading-spinner';

// Import all page components that can be shown in split view
import HomePage from '@/pages/HomePage';
import LibraryPage from '@/pages/LibraryPage';
import ObjectPage from '@/pages/ObjectPage';
import TemplatePage from '@/pages/TemplatePage';
import BlueprintItemPage from '@/pages/TemplateItemPage';
import DocumentsPage from '@/pages/DocumentsPage';
import DocumentDetailPage from '@/pages/DocumentDetailPage';
import DialogPage from '@/pages/DialogPage';
import DialogItemPage from '@/pages/DialogItemPage';
import LogicPage from '@/pages/LogicPage';
import LogicFlowDetailPage from '@/pages/LogicFlowDetailPage';
import TMSPage from '@/pages/TMSPage';
import TypesAndTagsPage from '@/pages/TypesAndTagsPage';
import ImagesPage from '@/pages/ImagesPage';
import RelationshipPage from '@/pages/RelationshipPage';
import SettingsPage from '@/pages/SettingsPage';
import UIPage from '@/pages/UIPage';

/**
 * Context for providing virtual route params to split view pages.
 * Pages in split view can use useSplitViewParams() to get params extracted from the path.
 */
interface SplitViewContextType {
  params: Record<string, string>;
  path: string;
  isSplitView: boolean;
}

export const SplitViewContext = createContext<SplitViewContextType | null>(null);

/**
 * Hook to get split view params. Use this in page components that need to work in split view.
 * Returns null if not in a split view context.
 */
export const useSplitViewContext = () => useContext(SplitViewContext);

// Define route configurations for split view rendering
// Order matters - more specific paths should come first
const SPLIT_VIEW_ROUTES: Array<{
  path: string;
  component: React.ComponentType<any>;
  paramKeys?: string[];
}> = [
  { path: '/library/:itemId', component: ObjectPage, paramKeys: ['itemId'] },
  { path: '/library', component: LibraryPage },
  { path: '/templates/:itemId', component: BlueprintItemPage, paramKeys: ['itemId'] },
  { path: '/templates', component: TemplatePage },
  { path: '/documents/:documentId', component: DocumentDetailPage, paramKeys: ['documentId'] },
  { path: '/documents', component: DocumentsPage },
  { path: '/dialogs/:dialogId', component: DialogItemPage, paramKeys: ['dialogId'] },
  { path: '/dialogs', component: DialogPage },
  { path: '/logic/:flowId', component: LogicFlowDetailPage, paramKeys: ['flowId'] },
  { path: '/logic', component: LogicPage },
  { path: '/tms', component: TMSPage },
  { path: '/types-and-tags', component: TypesAndTagsPage },
  { path: '/images', component: ImagesPage },
  { path: '/relationship', component: RelationshipPage },
  { path: '/settings', component: SettingsPage },
  { path: '/ui', component: UIPage },
  { path: '/home', component: HomePage },
];

interface SplitViewContentProps {
  path: string;
  className?: string;
}

/**
 * SplitViewContent renders the appropriate page component based on the given path.
 * This is used in split view mode to show the content side-by-side with the main content.
 * 
 * Provides a SplitViewContext with extracted route params so pages can access them
 * via useSplitViewContext() hook.
 */
export const SplitViewContent: React.FC<SplitViewContentProps> = ({ path, className }) => {
  // Find the matching route and extract params
  const matchedRoute = useMemo(() => {
    for (const route of SPLIT_VIEW_ROUTES) {
      const match = matchPath(route.path, path);
      if (match) {
        return {
          route,
          params: match.params as Record<string, string>,
        };
      }
    }
    return null;
  }, [path]);

  if (!matchedRoute) {
    return (
      <div className={className}>
        <div className="p-4 text-sm text-muted-foreground">
          No content available for path: {path}
        </div>
      </div>
    );
  }

  const { route, params } = matchedRoute;
  const PageComponent = route.component;

  // Create the context value
  const contextValue: SplitViewContextType = useMemo(() => ({
    params,
    path,
    isSplitView: true,
  }), [params, path]);

  return (
    <div className={className}>
      <Suspense fallback={
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="md" text="Loading..." />
        </div>
      }>
        <SplitViewContext.Provider value={contextValue}>
          <PageComponent />
        </SplitViewContext.Provider>
      </Suspense>
    </div>
  );
};

export default SplitViewContent;

